import { useEffect, useState } from 'react';
import { useI18n } from '../i18n';
import { Link } from '../router/Link';
import { useRouter } from '../router/RouterContext';

const RANNA_NAV_ITEMS = [
  { path: '/rana', label: '홈' },
  { path: '/business', label: '사업자 정보' },
  { path: '/legal', label: '법무' },
];

const GBT_ANCHOR_ITEMS = [
  { href: '#features', label: '기능' },
  { href: '#screenshots', label: '스크린샷' },
  { href: '#download', label: '다운로드' },
];

const GBT_ROUTE_ITEMS = [
  { path: '/legal', label: '법무' },
];

export function TopNav() {
  const { t, locale, setLocale } = useI18n();
  const { path } = useRouter();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    setOpen(false);
  }, [path]);

  const isRanna = path.startsWith('/rana');
  const isRoot = path === '/';
  const showHamburger = isRanna || isRoot;

  return (
    <header
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 50,
        borderBottom: '1px solid var(--stroke)',
        background: 'rgba(247,247,251,0.88)',
        backdropFilter: 'blur(14px)',
        WebkitBackdropFilter: 'blur(14px)',
      }}
    >
      <nav
        style={{
          maxWidth: '72rem',
          margin: '0 auto',
          height: '52px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0 1.5rem',
        }}
      >
        {/* Logo + desktop nav */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '2rem' }}>
          {isRanna ? (
            /* RANNA logo */
            <Link
              to="/rana"
              aria-label="RANNA 홈으로 이동"
              style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', textDecoration: 'none' }}
            >
              <span aria-hidden style={{ display: 'flex', gap: '3px', alignItems: 'center' }}>
                <span style={{ width: 8, height: 8, borderRadius: '50%', display: 'inline-block', background: '#4A7FB7', boxShadow: '0 0 7px rgba(74,127,183,0.5)' }} />
                <span style={{ width: 8, height: 8, borderRadius: '50%', display: 'inline-block', background: '#FFC843', boxShadow: '0 0 7px rgba(255,200,67,0.5)' }} />
              </span>
              <span style={{ fontFamily: 'var(--font-display)', fontSize: '1.1rem', letterSpacing: '0.1em', color: 'var(--text)' }}>
                RANNA
              </span>
            </Link>
          ) : (
            /* GBT logo */
            <Link
              to="/"
              aria-label="GIRLSBAND TABI 홈으로 이동"
              style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', textDecoration: 'none' }}
            >
              <span
                aria-hidden
                style={{
                  display: 'inline-block',
                  width: 28,
                  height: 14,
                  borderRadius: '999px',
                  background: 'linear-gradient(135deg, #4A7FB7, #FFC843)',
                }}
              />
              <span style={{ fontFamily: 'var(--font-display)', fontSize: '0.95rem', letterSpacing: '0.12em', color: 'var(--text)' }}>
                GIRLSBAND TABI
              </span>
            </Link>
          )}

          {/* Desktop nav links */}
          {isRanna ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem' }} className="nav-desktop">
              {RANNA_NAV_ITEMS.map((item) => {
                const isActive = path === item.path || (item.path !== '/rana' && path.startsWith(item.path));
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    aria-current={isActive ? 'page' : undefined}
                    style={{
                      fontSize: '0.7rem',
                      letterSpacing: '0.1em',
                      textTransform: 'uppercase',
                      fontWeight: 600,
                      color: isActive ? '#FFC843' : 'var(--muted)',
                      textDecoration: 'none',
                      paddingBottom: '2px',
                      borderBottom: isActive ? '1px solid #FFC843' : '1px solid transparent',
                    }}
                  >
                    {item.label}
                  </Link>
                );
              })}
            </div>
          ) : isRoot ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem' }} className="nav-desktop">
              {GBT_ANCHOR_ITEMS.map((item) => (
                <a
                  key={item.href}
                  href={item.href}
                  style={{
                    fontSize: '0.7rem',
                    letterSpacing: '0.1em',
                    textTransform: 'uppercase',
                    fontWeight: 600,
                    color: 'var(--muted)',
                    textDecoration: 'none',
                    paddingBottom: '2px',
                    borderBottom: '1px solid transparent',
                  }}
                >
                  {item.label}
                </a>
              ))}
              {GBT_ROUTE_ITEMS.map((item) => {
                const isActive = path.startsWith(item.path);
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    aria-current={isActive ? 'page' : undefined}
                    style={{
                      fontSize: '0.7rem',
                      letterSpacing: '0.1em',
                      textTransform: 'uppercase',
                      fontWeight: 600,
                      color: isActive ? '#4A7FB7' : 'var(--muted)',
                      textDecoration: 'none',
                      paddingBottom: '2px',
                      borderBottom: isActive ? '1px solid #4A7FB7' : '1px solid transparent',
                    }}
                  >
                    {item.label}
                  </Link>
                );
              })}
            </div>
          ) : null}
        </div>

        {/* Right actions */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }} className="nav-actions-desktop">
          <label className="sr-only" htmlFor="lang-select">{t('common.language')}</label>
          <select
            id="lang-select"
            value={locale}
            onChange={(e) => setLocale(e.target.value as typeof locale)}
            aria-label={t('common.aria.languageMenu')}
            style={{
              background: 'transparent',
              border: '1px solid var(--stroke)',
              borderRadius: '4px',
              color: 'var(--muted)',
              fontSize: '0.7rem',
              letterSpacing: '0.06em',
              fontWeight: 600,
              padding: '3px 6px',
              cursor: 'pointer',
            }}
          >
            <option value="ko">KO</option>
            <option value="ja">JP</option>
            <option value="en">EN</option>
          </select>
        </div>

        {/* Mobile hamburger — shown only when there are nav items */}
        {showHamburger && (
          <button
            type="button"
            onClick={() => setOpen((p) => !p)}
            aria-expanded={open}
            aria-label={open ? t('common.aria.closeNav') : t('common.aria.openNav')}
            className="nav-hamburger"
            style={{
              width: '36px',
              height: '36px',
              display: 'none',
              alignItems: 'center',
              justifyContent: 'center',
              border: '1px solid var(--stroke)',
              borderRadius: '4px',
              background: 'transparent',
              color: 'var(--muted)',
              cursor: 'pointer',
              fontSize: '1rem',
            }}
          >
            {open ? '✕' : '≡'}
          </button>
        )}
      </nav>

      {/* Mobile drawer */}
      {open && (
        <div
          style={{
            background: 'rgba(247,247,251,0.97)',
            backdropFilter: 'blur(14px)',
            borderBottom: '1px solid var(--stroke)',
            padding: '0.75rem 1.5rem 1rem',
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
            {isRanna ? (
              RANNA_NAV_ITEMS.map((item) => {
                const isActive = path === item.path || (item.path !== '/rana' && path.startsWith(item.path));
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    aria-current={isActive ? 'page' : undefined}
                    style={{
                      display: 'block',
                      padding: '10px 0',
                      fontSize: '0.75rem',
                      letterSpacing: '0.1em',
                      textTransform: 'uppercase',
                      fontWeight: 600,
                      color: isActive ? '#FFC843' : 'var(--muted)',
                      borderBottom: '1px solid var(--stroke-subtle)',
                      textDecoration: 'none',
                    }}
                  >
                    {item.label}
                  </Link>
                );
              })
            ) : (
              <>
                {GBT_ANCHOR_ITEMS.map((item) => (
                  <a
                    key={item.href}
                    href={item.href}
                    onClick={() => setOpen(false)}
                    style={{
                      display: 'block',
                      padding: '10px 0',
                      fontSize: '0.75rem',
                      letterSpacing: '0.1em',
                      textTransform: 'uppercase',
                      fontWeight: 600,
                      color: 'var(--muted)',
                      borderBottom: '1px solid var(--stroke-subtle)',
                      textDecoration: 'none',
                    }}
                  >
                    {item.label}
                  </a>
                ))}
                {GBT_ROUTE_ITEMS.map((item) => {
                  const isActive = path.startsWith(item.path);
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      aria-current={isActive ? 'page' : undefined}
                      style={{
                        display: 'block',
                        padding: '10px 0',
                        fontSize: '0.75rem',
                        letterSpacing: '0.1em',
                        textTransform: 'uppercase',
                        fontWeight: 600,
                        color: isActive ? '#4A7FB7' : 'var(--muted)',
                        borderBottom: '1px solid var(--stroke-subtle)',
                        textDecoration: 'none',
                      }}
                    >
                      {item.label}
                    </Link>
                  );
                })}
              </>
            )}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.75rem' }}>
            <select
              value={locale}
              aria-label={t('common.language')}
              onChange={(e) => setLocale(e.target.value as typeof locale)}
              style={{
                flex: 1,
                background: 'var(--card)',
                border: '1px solid var(--stroke)',
                borderRadius: '4px',
                color: 'var(--muted)',
                fontSize: '0.75rem',
                padding: '8px 10px',
              }}
            >
              <option value="ko">한국어</option>
              <option value="ja">日本語</option>
              <option value="en">English</option>
            </select>
          </div>
        </div>
      )}

      <style>{`
        @media (min-width: 768px) {
          .nav-desktop { display: flex !important; }
          .nav-actions-desktop { display: flex !important; }
          .nav-hamburger { display: none !important; }
        }
        @media (max-width: 767px) {
          .nav-desktop { display: none !important; }
          .nav-actions-desktop { display: none !important; }
          .nav-hamburger { display: flex !important; }
        }
      `}</style>
    </header>
  );
}
